import { useRef, useCallback, useEffect, useState } from 'react';
import { MagnifyingGlass, PlusCircle } from 'phosphor-react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './order-service-list.module.css';
import { removeObjectFromLocal } from '../../../../utils/localStorage';
import { DescriptionTable } from './components/DescriptionTable';
import { DeleteModal } from './components/DeleteModal';
// import { Repair } from '../../History/interfaces';
import { Button } from '../../../../shared/Button';
import { useRepairData } from './useRepairData';
import { FinishModal } from './components/FinishModal';
import { PageLayout } from '../../../../shared/PageLayout';
import { RepairTable } from './OrderServiceTable';
import useRemoveModal from './hooks/useRemoveModal';
import useSelectedRepair from './hooks/useSelectedRepair';
import useFinishModal from './hooks/useFinishModal';
import { FeedbackModal } from '../../../../shared/FeedbackModal';
import { InputComponent } from '../../../../shared/InputComponent';

export const OrderServiceList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filteredRepairs,
    loadMoreData,
    repairs,
    searchQuery,
    setSearchQuery,
    isLoading,
    isFetchingRepairs,
    deleteRepair,
    finishRepair,
    refetchFilteredSearch,
  } = useRepairData();

  const actionsRef = useRef<HTMLDivElement>(null);
  const descriptionTableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [tableHeight, setTableHeight] = useState(400);

  const removeModal = useRemoveModal();
  const finishModal = useFinishModal();
  const selectedRepair = useSelectedRepair();

  const onFinish = useCallback(async () => {
    await finishRepair(selectedRepair.selectedRepair?.id_repair ?? '');
    setSearchQuery('');
  }, [finishRepair, selectedRepair.selectedRepair?.id_repair, setSearchQuery]);

  const onDelete = useCallback(async () => {
    await deleteRepair(selectedRepair.selectedRepair?.id_repair ?? '');
    setSearchQuery('');
  }, [deleteRepair, selectedRepair.selectedRepair?.id_repair, setSearchQuery]);

  useEffect(() => {
    const updateTableHeight = () => {
      const containerHeight = containerRef.current?.offsetHeight ?? 0;
      const actionsHeight = actionsRef.current?.offsetHeight ?? 0;
      const descriptionTableHeight =
        descriptionTableRef.current?.offsetHeight ?? 0;
      setTableHeight(containerHeight - actionsHeight - descriptionTableHeight);
    };

    updateTableHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (actionsRef.current) {
      resizeObserver.observe(actionsRef.current);
    }

    if (descriptionTableRef.current) {
      resizeObserver.observe(descriptionTableRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Atualiza a URL com a query de pesquisa
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('q', searchQuery);
    } else {
      params.delete('q');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate, searchQuery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearchQuery(query.toUpperCase());
  }, []); // carrega a query da URL apenas uma vez no início

  return (
    <PageLayout.Root title="ORDEM DE SERVIÇO">
      <FeedbackModal isOpen={isFetchingRepairs} variant="sending" />

      {repairs.length === 0 && (
        <FeedbackModal isOpen={isLoading} variant="sending" />
      )}

      {removeModal.isOpen && (
        <DeleteModal
          onDelete={onDelete}
          onClose={() => {
            removeModal.onClose();
          }}
          repair={selectedRepair.selectedRepair}
        />
      )}

      {finishModal.isOpen && (
        <FinishModal
          onFinish={onFinish}
          onClose={() => {
            finishModal.onClose();
          }}
          repair={selectedRepair.selectedRepair}
        />
      )}

      <section style={{ height: '100%' }}>
        <div className={styles['order-service-list']} ref={containerRef}>
          <div className={styles['order-service-list-header']}>
            <div className={styles.actions} ref={actionsRef}>
              <InputComponent
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value.toUpperCase());
                }}
                placeholder="Pesquisar por OS, Cliente, ou Placa"
                icon={
                  <button
                    type="button"
                    onClick={() => {
                      refetchFilteredSearch();
                    }}
                  >
                    <MagnifyingGlass
                      size="1.6rem"
                      color="#A5A5A5"
                      weight="bold"
                      className=""
                    />
                  </button>
                }
                className={styles.search}
              />

              <div className={styles.info}>
                <div className={styles.desktop}>
                  <DescriptionTable />
                </div>
                <div className={styles.button}>
                  <Button
                    handleClick={() => {
                      removeObjectFromLocal('@local-order-service');
                      navigate(`/new-order-service-form`);
                    }}
                    style={{
                      fontWeight: 700,
                      padding: '1rem',
                      boxShadow: '0px 2px 2px #00000044',
                      fontSize: '1.2rem',
                    }}
                  >
                    <PlusCircle size="2rem" /> Nova O.S.
                  </Button>
                </div>
              </div>
            </div>
            <div className={styles.mobile} ref={descriptionTableRef}>
              <DescriptionTable />
            </div>
          </div>
          <RepairTable
            repairs={searchQuery.trim().length > 0 ? filteredRepairs : repairs}
            onScroll={() => loadMoreData()}
            isLoading={isLoading}
            height={tableHeight ?? 400}
          />
        </div>
      </section>
    </PageLayout.Root>
  );
};
